import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search,
  RefreshCw,
  Clock,
  User,
  FileText,
  Flag,
  CheckSquare,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Action badge colors
const ACTION_COLORS = {
  RED_FLAG_CREATE: 'bg-red-100 text-red-700 border-red-200',
  RED_FLAG_ACKNOWLEDGE: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  RED_FLAG_DELETE: 'bg-slate-100 text-slate-700 border-slate-200',
  FORMATTING_REQ_CREATE: 'bg-blue-100 text-blue-700 border-blue-200',
  FORMATTING_REQ_UPDATE: 'bg-amber-100 text-amber-700 border-amber-200',
  FORMATTING_REQ_DELETE: 'bg-slate-100 text-slate-700 border-slate-200',
  COMPLIANCE_COMPLETE: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  DEADLINE_CREATE: 'bg-purple-100 text-purple-700 border-purple-200',
  DEADLINE_UPDATE: 'bg-amber-100 text-amber-700 border-amber-200',
  GAP_CREATE: 'bg-orange-100 text-orange-700 border-orange-200',
  GAP_UPDATE: 'bg-amber-100 text-amber-700 border-amber-200',
  SLOT_GRANT: 'bg-teal-100 text-teal-700 border-teal-200',
  CREATE: 'bg-blue-100 text-blue-700 border-blue-200',
  UPDATE: 'bg-amber-100 text-amber-700 border-amber-200',
  DELETE: 'bg-red-100 text-red-700 border-red-200',
};

// Action icons
const ACTION_ICONS = {
  RED_FLAG_CREATE: Flag,
  RED_FLAG_ACKNOWLEDGE: CheckSquare,
  RED_FLAG_DELETE: Flag,
  COMPLIANCE_COMPLETE: CheckSquare,
  default: FileText
};

function formatAction(action) {
  return action.replace(/_/g, ' ');
}

function formatTimestamp(iso) {
  const date = new Date(iso);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

export default function AuditLogPage() {
  const { getAuthHeaders } = useAuth();
  
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [availableActions, setAvailableActions] = useState([]);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      let url = `${API_URL}/api/admin/audit-logs?page=${page}&page_size=${pageSize}`;
      if (actionFilter) {
        url += `&action_filter=${actionFilter}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.items);
        setTotal(data.total);
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, actionFilter, getAuthHeaders]);

  const fetchActions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/audit-logs/actions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableActions(data.actions);
      }
    } catch (err) {
      console.error('Failed to fetch actions:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchLogs();
    fetchActions();
  }, [fetchLogs, fetchActions]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div data-testid="audit-log-page">
      <PageHeader 
        title="Audit Logs"
        description="Track all Strategy Core actions across the platform"
      />
      
      <div className="p-8">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {/* Action Filter */}
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-64" data-testid="action-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filter by action..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {availableActions.map((action) => (
                  <SelectItem key={action} value={action}>
                    {formatAction(action)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Stats */}
            <div className="text-sm text-slate-500">
              {total} total entries
            </div>
          </div>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchLogs()}
            disabled={isLoading}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Log Table */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Clock className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-500">No audit logs found</p>
                <p className="text-sm text-slate-400 mt-1">
                  Logs will appear when Strategy Core actions are performed
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        User
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Pursuit
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => {
                      const ActionIcon = ACTION_ICONS[log.action] || ACTION_ICONS.default;
                      const colorClass = ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700 border-slate-200';
                      
                      return (
                        <tr 
                          key={log.id}
                          className={cn(
                            "border-b border-slate-100 hover:bg-slate-50 transition-colors",
                            index % 2 === 0 && "bg-white",
                            index % 2 === 1 && "bg-slate-25"
                          )}
                          data-testid={`audit-row-${log.id}`}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-sm text-slate-700 font-mono">
                                {formatTimestamp(log.timestamp)}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                                <User className="w-3.5 h-3.5 text-slate-600" />
                              </div>
                              <span className="text-sm text-slate-700">
                                {log.user_email}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={cn("text-xs font-medium border", colorClass)}>
                              <ActionIcon className="w-3 h-3 mr-1" />
                              {formatAction(log.action)}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {log.pursuit_name ? (
                              <span className="text-sm text-slate-700">
                                {log.pursuit_name}
                              </span>
                            ) : (
                              <span className="text-sm text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-sm text-slate-600">
                              {log.description || '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-slate-500">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
